// src/pages/Admin/Positions/TeamPositionManagement.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Check, X, Pencil, Trash2, EyeOff, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import ConfirmDialog from '../../../components/Admin/ConfirmDialog';
import adminService, { type TeamPosition } from '../../../services/adminService';
import { useToast } from '../../../hooks/useToast';

/**
 * Keeping the list of jobs somebody can hold on the team.
 *
 * Positions were a Java enum, so the seven that existed were the seven that could ever
 * exist and adding one took a deploy. This is the screen that replaced that.
 *
 * Three things on this screen are easy to confuse, so they are kept visibly apart:
 *
 * - Renaming changes the label only. The key underneath is what each person's record
 *   holds, and it is never renamed, because doing so would unassign every holder while
 *   looking like a cosmetic edit. The key is shown next to the label so it is clear that
 *   something fixed exists behind the name.
 * - Retiring takes a position out of the dropdown and leaves the title on everybody who
 *   already holds it. This is what "we do not appoint these any more" means.
 * - Deleting removes it entirely, and is refused while anybody holds it. The server says
 *   how many people do and suggests retiring instead; that message is shown as given.
 *
 * There is deliberately no Order column, though `displayOrder` comes back on every
 * position and the rows are sorted by it.
 *
 * It was shown as a raw number - 10, 20, 30 - and the spacing was there by the usual
 * convention: gaps let you insert between two rows by writing a value in between, instead
 * of renumbering everything below. That convention assumes somebody can type an order.
 * Nobody can here. The arrows send the whole list and the server renumbers it from
 * scratch, so no insert-between ever happens, and the step could be 1 instead of 10 with
 * no visible difference.
 *
 * So the column displayed a number with no meaning an admin could act on, and explaining
 * it required describing a capability the screen does not have. It was asked about three
 * times before being removed. If a reason to show an order appears later, the honest
 * version is an editable field, not a read-only number.
 *
 * A plain table rather than DataTable. There are a handful of positions, they are not
 * searched or paginated, and the actions here are not the same shape as DataTable's row
 * actions - putting something in that array that was not an icon button is what produced
 * the dead seventh icon on the users screen.
 */
const TeamPositionManagement: React.FC = () => {
  const { showToast } = useToast();
  const [positions, setPositions] = useState<TeamPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');

  const [confirmDelete, setConfirmDelete] = useState<TeamPosition | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setPositions(await adminService.getAllTeamPositions());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load the positions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setAdding(true);
    try {
      // Only the label is sent. The key is derived server-side so two admins typing the
      // same label cannot create two positions.
      const created = await adminService.createTeamPosition(label);
      setNewLabel('');
      showToast(`${created.label} added`, 'success');
      await load();
    } catch (err) {
      // The server explains its refusals - an existing name, a retired one to reactivate
      // instead, a label too long to make a key from - so it is shown rather than
      // replaced with something vaguer.
      showToast(err instanceof Error ? err.message : 'Could not add the position', 'error');
    } finally {
      setAdding(false);
    }
  };

  const saveLabel = async (position: TeamPosition) => {
    const label = editingLabel.trim();
    if (!label || label === position.label) {
      setEditingId(null);
      return;
    }
    setBusyId(position.id);
    try {
      await adminService.updateTeamPosition(position.id, { label });
      setEditingId(null);
      showToast('Renamed', 'success');
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not rename it', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const setActive = async (position: TeamPosition, active: boolean) => {
    setBusyId(position.id);
    try {
      await adminService.updateTeamPosition(position.id, { active });
      showToast(active ? `${position.label} is available again` : `${position.label} retired`, 'success');
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not change it', 'error');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Moves a position up or down.
   *
   * Sends the whole list in its new order and lets the server renumber it, rather than
   * swapping two neighbours' numbers. Swapping assumed the numbers were distinct: every
   * position added here used to land on the same default, and the arrows then did nothing
   * at all, because each write set the value the row already held.
   */
  const move = async (index: number, direction: -1 | 1) => {
    const neighbour = positions[index + direction];
    if (!neighbour) return;

    const reordered = [...positions];
    [reordered[index], reordered[index + direction]] = [reordered[index + direction], reordered[index]];

    setBusyId(positions[index].id);
    try {
      // Shown from the response rather than reloaded, so the numbers on screen are the
      // ones the server actually wrote.
      setPositions(await adminService.reorderTeamPositions(reordered));
    } catch (err) {
      // A 409 here means somebody else reordered while this page was open, and the
      // server's message says to reload. Shown for longer than the usual toast because
      // it asks the admin to do something, and the reload below changes the list under
      // them - without the explanation that would look like their click did nothing.
      showToast(err instanceof Error ? err.message : 'Could not reorder them', 'error', 8000);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (position: TeamPosition) => {
    setBusyId(position.id);
    try {
      await adminService.deleteTeamPosition(position.id);
      showToast(`${position.label} deleted`, 'success');
      await load();
    } catch (err) {
      // "3 people hold this position. Retire it instead, which hides it from the dropdown
      // and leaves their title intact." That is the whole answer, so it is shown whole,
      // and for long enough to read.
      showToast(err instanceof Error ? err.message : 'Could not delete it', 'error', 8000);
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Team Positions</h1>
        <p className="mt-1 text-sm text-gray-600">
          The jobs an admin or staff member can hold. These fill the dropdown when somebody
          is given a staff role, so anything added here is available straight away. The
          arrows set the order they appear in.
        </p>
      </div>

      {/* Adding */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor="new-position">
          Add a position
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id="new-position"
            type="text"
            value={newLabel}
            maxLength={80}
            placeholder="Gaffer"
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500"
          />
          <button
            type="button"
            onClick={() => void add()}
            disabled={adding || !newLabel.trim()}
            className="inline-flex items-center justify-center rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            <Plus className="mr-2 h-4 w-4" />
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Type the name as it should read. The short key stored against each person is made
          from it automatically and never changes afterwards.
        </p>
      </div>

      {loadError && (
        <div data-testid="positions-load-error" className="rounded-md bg-red-50 p-4 text-sm text-red-800">
          <p>{loadError}</p>
          <button type="button" onClick={() => void load()} className="mt-2 font-medium underline hover:no-underline">
            Try again
          </button>
        </div>
      )}

      {loading && !loadError && <p className="text-sm text-gray-500">Loading positions…</p>}

      {!loading && !loadError && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Position</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {positions.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                    No positions yet. Add the first one above.
                  </td>
                </tr>
              )}
              {positions.map((p, index) => (
                <tr key={p.id} className={p.active ? undefined : 'bg-gray-50'}>
                  <td className="px-4 py-3">
                    {editingId === p.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editingLabel}
                          maxLength={80}
                          autoFocus
                          aria-label={`New name for ${p.label}`}
                          onChange={(e) => setEditingLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void saveLabel(p);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                        <button type="button" aria-label="Save name" onClick={() => void saveLabel(p)} className="p-1 text-green-700 hover:text-green-900">
                          <Check className="h-4 w-4" />
                        </button>
                        <button type="button" aria-label="Cancel rename" onClick={() => setEditingId(null)} className="p-1 text-gray-500 hover:text-gray-700">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-900">{p.label}</span>
                        {/* The stored key, shown because it is what the rename does not
                            touch. Without it a rename looks like it changes everything. */}
                        <span className="ml-2 font-mono text-xs text-gray-400">{p.name}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {p.active ? (
                      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Available
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                        Retired
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        aria-label={`Move ${p.label} up`}
                        disabled={index === 0 || busyId === p.id}
                        onClick={() => void move(index, -1)}
                        className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${p.label} down`}
                        disabled={index === positions.length - 1 || busyId === p.id}
                        onClick={() => void move(index, 1)}
                        className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Rename ${p.label}`}
                        disabled={busyId === p.id}
                        onClick={() => { setEditingId(p.id); setEditingLabel(p.label); }}
                        className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {p.active ? (
                        <button
                          type="button"
                          aria-label={`Retire ${p.label}`}
                          title="Hide from the dropdown, leaving the title on anybody who holds it"
                          disabled={busyId === p.id}
                          onClick={() => void setActive(p, false)}
                          className="p-1 text-gray-400 hover:text-amber-600 disabled:opacity-30"
                        >
                          <EyeOff className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Make ${p.label} available again`}
                          disabled={busyId === p.id}
                          onClick={() => void setActive(p, true)}
                          className="p-1 text-gray-400 hover:text-green-600 disabled:opacity-30"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label={`Delete ${p.label}`}
                        disabled={busyId === p.id}
                        onClick={() => setConfirmDelete(p)}
                        className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        title={`Delete ${confirmDelete?.label ?? ''}?`}
        message={
          'This removes the position entirely. If anybody holds it the deletion will be refused - '
          + 'retire it instead, which hides it from the dropdown and leaves their title intact.'
        }
        confirmText="Delete"
        danger
        loading={busyId !== null && busyId === confirmDelete?.id}
        onConfirm={() => { if (confirmDelete) void remove(confirmDelete); }}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default TeamPositionManagement;
